import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Plus, Search, Eye, Upload, FileText, Loader2, Truck, Check, X, AlertTriangle, History, Package, Trash2 } from 'lucide-react';
import { salesReturnAPI, orderConfirmationAPI, customerAPI, branchAPI } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import { usePermissions } from '../../hooks/usePermissions';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import Pagination from '../ui/Pagination';
import CustomerSearchInput from '../ui/CustomerSearchInput';
import ProductSearchInput from '../ui/ProductSearchInput';

const statusColors = {
  Draft: 'bg-gray-100 text-gray-600',
  Verified: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Closed: 'bg-purple-100 text-purple-700',
};

const RETURN_REASONS = [
  'Defective/Damaged goods',
  'Wrong item shipped',
  'Excess quantity',
  'Customer rejection (quality not met)',
];

const STOCK_ACTIONS = ['Restock', 'Quarantine', 'Scrap'];

const emptyItem = { product_name: '', product_id: '', description: '', qty: 1, unit: 'PCS', rate: 0, reason: RETURN_REASONS[0], stock_action: 'Restock' };

const SalesReturnModule = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | detail | create
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { hasPermission } = usePermissions();

  // Create form state
  const [sourceMode, setSourceMode] = useState(null); // 'oc' | 'standalone'
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [ocs, setOcs] = useState([]);
  const [ocSearch, setOcSearch] = useState('');
  const [ocLoading, setOcLoading] = useState(false);
  const [selectedOC, setSelectedOC] = useState('');
  const [form, setForm] = useState({
    customer_id: '', customer_name: '', branch: '', linked_oc_id: '', linked_oc_number: '',
    items: [{ ...emptyItem }], notes: '', date: new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard

  // Action dialog
  const [actionDialog, setActionDialog] = useState(null); // { type: 'verify'|'approve', sr: {} }
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Upload
  const [uploading, setUploading] = useState(false);

  const canCreate = hasPermission('sales_return.create');
  const canVerify = hasPermission('sales_return.verify');
  const canApprove = hasPermission('sales_return.approve');

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await salesReturnAPI.list(params);
      setReturns(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total || 0);
    } catch (err) {
      toast.error('Failed to load sales returns');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const loadFormData = async () => {
    try {
      const [custRes, branchRes] = await Promise.all([
        customerAPI.getAll(),
        branchAPI.getAll(),
      ]);
      setCustomers(Array.isArray(custRes) ? custRes : []);
      setBranches(Array.isArray(branchRes) ? branchRes : []);
      setOcs([]);
    } catch (err) {
      toast.error('Failed to load form data');
    }
  };

  // Server-side OC search (debounced) — finds ANY dispatched/partially-dispatched OC,
  // not just the first page. Avoids the old 200-item client-side cap.
  useEffect(() => {
    if (sourceMode !== 'oc') return;
    const q = (ocSearch || '').trim();
    if (q.length < 2) { setOcs([]); setOcLoading(false); return; }
    let cancelled = false;
    setOcLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await orderConfirmationAPI.getAll({ page: 1, page_size: 50, search: q });
        const items = Array.isArray(res) ? res : (res?.items || []);
        if (!cancelled) setOcs(items);
      } catch {
        if (!cancelled) setOcs([]);
      } finally {
        if (!cancelled) setOcLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ocSearch, sourceMode]);

  const handleCreateFromOC = async () => {
    await loadFormData();
    setSourceMode('oc');
    setForm({ customer_id: '', customer_name: '', branch: '', linked_oc_id: '', linked_oc_number: '', items: [{ ...emptyItem }], notes: '', date: new Date().toISOString().slice(0, 10) });
    setSelectedOC('');
    setView('create');
  };

  const handleCreateStandalone = async () => {
    await loadFormData();
    setSourceMode('standalone');
    setForm({ customer_id: '', customer_name: '', branch: '', linked_oc_id: '', linked_oc_number: '', items: [{ ...emptyItem }], notes: '', date: new Date().toISOString().slice(0, 10) });
    setView('create');
  };

  const handleOCSelect = async (ocId) => {
    if (!ocId) return;
    setSelectedOC(ocId);
    try {
      const data = await salesReturnAPI.getOCItems(ocId);
      const items = (data.items || []).map(it => ({
        product_name: it.product_name,
        product_id: it.product_id || '',
        description: it.description || '',
        hsn_code: it.hsn_code || '',
        original_qty: it.original_qty || 0,
        qty: it.original_qty || 1,
        unit: it.unit || 'PCS',
        rate: it.rate || 0,
        reason: RETURN_REASONS[0],
        stock_action: 'Restock',
      }));
      setForm(prev => ({
        ...prev,
        customer_id: data.customer_id || '',
        customer_name: data.customer_name || '',
        branch: data.branch || '',
        linked_oc_id: ocId,
        linked_oc_number: data.oc_number || '',
        items: items.length > 0 ? items : [{ ...emptyItem }],
      }));
    } catch (err) {
      toast.error('Failed to fetch OC items');
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicates
    if (!form.customer_name) return toast.error('Please select a customer');
    if (form.items.length === 0 || !form.items[0].product_name) return toast.error('Add at least one item');
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload = { ...form };
      await salesReturnAPI.create(payload);
      toast.success('Sales return created');
      setView('list');
      fetchReturns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sales return');
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const handleViewDetail = async (sr) => {
    try {
      const full = await salesReturnAPI.get(sr.id);
      setSelectedReturn(full);
      if (branches.length === 0) {
        try { const br = await branchAPI.getAll(); setBranches(Array.isArray(br) ? br : []); } catch (_) {}
      }
      setView('detail');
    } catch (err) {
      toast.error('Failed to load return details');
    }
  };

  const handleAction = async (actionType) => {
    setActionLoading(true);
    try {
      const payload = { action: actionType === 'reject' ? 'reject' : actionType, notes: actionNotes };
      if (actionDialog.type === 'verify') {
        await salesReturnAPI.verify(actionDialog.sr.id, payload);
      } else {
        await salesReturnAPI.approve(actionDialog.sr.id, payload);
      }
      toast.success(`Sales return ${actionType === 'reject' ? 'rejected' : actionType === 'verify' ? 'verified' : 'approved'}`);
      setActionDialog(null);
      setActionNotes('');
      // Refresh detail
      const updated = await salesReturnAPI.get(actionDialog.sr.id);
      setSelectedReturn(updated);
      fetchReturns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedReturn) return;
    setUploading(true);
    try {
      await salesReturnAPI.uploadDocument(selectedReturn.id, file);
      toast.success('Document uploaded');
      const updated = await salesReturnAPI.get(selectedReturn.id);
      setSelectedReturn(updated);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDoc = async (docIdx) => {
    if (!selectedReturn) return;
    try {
      await salesReturnAPI.removeDocument(selectedReturn.id, docIdx);
      toast.success('Document removed');
      const updated = await salesReturnAPI.get(selectedReturn.id);
      setSelectedReturn(updated);
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };

  const updateItem = (index, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
  const removeItem = (i) => setForm(prev => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));

  // Fill product fields from the searchable picker (standalone returns)
  const selectItemProduct = (index, product) => {
    setForm(prev => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        product_id: product.id || '',
        product_name: product.name || '',
        unit: product.unit || items[index].unit || 'PCS',
        rate: product.selling_price || items[index].rate || 0,
      };
      return { ...prev, items };
    });
  };

  // ==================== LIST VIEW ====================
  if (view === 'list') {
    return (
      <div data-testid="sales-return-list">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Sales Returns</h2>
            <p className="text-[11px] text-gray-500">{totalItems} total returns</p>
          </div>
          {canCreate && (
            <div className="flex items-center gap-2">
              <button data-testid="sr-from-oc-btn" onClick={handleCreateFromOC}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                <FileText className="w-3.5 h-3.5" /> From OC
              </button>
              <button data-testid="sr-standalone-btn" onClick={handleCreateStandalone}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
                <Plus className="w-3.5 h-3.5" /> Standalone Return
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input data-testid="sr-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by SR#, customer, OC#..."
              className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
            {['all', 'Draft', 'Verified', 'Approved', 'Rejected', 'Closed'].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                data-testid={`sr-filter-${s.toLowerCase()}`}
                className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-colors ${statusFilter === s ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} /></div>
        ) : returns.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
            <Package className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm font-semibold text-gray-900 mb-1">No Sales Returns</p>
            <p className="text-xs text-gray-500">Create a return from an OC or manually</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full text-xs" data-testid="sr-table">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-[10px] uppercase tracking-wider">
                    <th className="text-left py-3 px-4 font-semibold">SR#</th>
                    <th className="text-left py-3 px-4 font-semibold">Customer</th>
                    <th className="text-left py-3 px-4 font-semibold">Linked OC</th>
                    <th className="text-left py-3 px-4 font-semibold">Date</th>
                    <th className="text-left py-3 px-4 font-semibold">Items</th>
                    <th className="text-left py-3 px-4 font-semibold">Status</th>
                    <th className="text-right py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {returns.map(sr => (
                    <tr key={sr.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4 font-semibold text-[#24549c]">{sr.sr_number}</td>
                      <td className="py-3 px-4 text-gray-700">{sr.customer_name}</td>
                      <td className="py-3 px-4 text-gray-500">{sr.linked_oc_number || '-'}</td>
                      <td className="py-3 px-4 text-gray-500">{sr.date}</td>
                      <td className="py-3 px-4 text-gray-500">{(sr.items || []).length} items</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[sr.status] || 'bg-gray-100'}`}>{sr.status}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => handleViewDetail(sr)} data-testid={`sr-view-${sr.sr_number}`}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700"><Eye className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <Pagination page={page} totalPages={totalPages} pageSize={pageSize}
                onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
            </div>
          </>
        )}
      </div>
    );
  }

  // ==================== CREATE VIEW ====================
  if (view === 'create') {
    return (
      <div data-testid="sales-return-create">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setView('list')} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>
              {sourceMode === 'oc' ? 'Return from OC' : 'Standalone Return'}
            </h2>
            <p className="text-[11px] text-gray-500">Create a new sales return</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-5">
          {/* OC Selection - Searchable (Dispatched + Partially Dispatched) */}
          {sourceMode === 'oc' && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Search Order Confirmation (Dispatched / Partially Dispatched)</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  data-testid="sr-oc-search"
                  value={ocSearch}
                  onChange={e => setOcSearch(e.target.value)}
                  placeholder="Type OC number or customer name to search..."
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
              </div>
              {ocSearch.trim().length >= 2 && (() => {
                const allowed = ['Dispatched', 'Partially Dispatched'];
                const matches = ocs.filter(oc => allowed.includes(oc.status));
                return (
                  <div className="mt-1 border border-gray-200 rounded-lg max-h-[160px] overflow-y-auto bg-white shadow-sm">
                    {ocLoading ? (
                      <div className="px-3 py-3 text-xs text-gray-400 text-center flex items-center justify-center gap-2" data-testid="sr-oc-loading">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...
                      </div>
                    ) : matches.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-gray-400 text-center">No dispatched / partially-dispatched OCs found</div>
                    ) : (
                      matches.map(oc => {
                        const isPartial = oc.status === 'Partially Dispatched';
                        return (
                          <button key={oc.id} type="button" data-testid={`sr-oc-option-${oc.id}`}
                            onClick={() => { handleOCSelect(oc.id); setOcSearch(oc.oc_number); }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-0 ${selectedOC === oc.id ? 'bg-blue-50 font-semibold' : ''}`}>
                            <span className="font-mono font-semibold" style={{ color: '#24549c' }}>{oc.oc_number}</span>
                            <span className="ml-2 text-gray-600">{oc.customer?.name || oc.customer_name || 'N/A'}</span>
                            <span className={`ml-2 text-[10px] font-semibold ${isPartial ? 'text-amber-600' : 'text-green-600'}`}>{oc.status}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                );
              })()}
              {selectedOC && form.linked_oc_number && (
                <div className="mt-1 text-[10px] text-blue-600 font-semibold">Selected: {form.linked_oc_number}</div>
              )}
            </div>
          )}

          {/* Customer & Branch */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Customer <span className="text-red-500">*</span></label>
              {sourceMode === 'oc' ? (
                <Input value={form.customer_name} readOnly className="bg-gray-50 text-xs" data-testid="sr-customer" />
              ) : (
                <CustomerSearchInput
                  value={form.customer_name}
                  customerId={form.customer_id}
                  onSelect={(c) => setForm(prev => ({ ...prev, customer_id: c.id, customer_name: c.name }))}
                  onClear={() => setForm(prev => ({ ...prev, customer_id: '', customer_name: '' }))}
                  placeholder="Search customer..."
                  testId="sr-customer-select"
                />
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Branch</label>
              <select data-testid="sr-branch" value={form.branch}
                onChange={e => setForm(prev => ({ ...prev, branch: e.target.value }))}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                <option value="">-- Select Branch --</option>
                {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Date</label>
              <Input type="date" data-testid="sr-date" value={form.date}
                onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))} className="text-xs" />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700">Return Items</label>
              {sourceMode === 'standalone' && (
                <button onClick={addItem} className="text-[10px] text-blue-600 hover:underline flex items-center gap-1" data-testid="sr-add-item">
                  <Plus className="w-3 h-3" /> Add Item
                </button>
              )}
            </div>
            {sourceMode === 'oc' && form.items.length > 1 && (
              <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="sr-oc-remove-hint">
                💡 If a dispatched item was NOT returned, click the red ✕ at the end of its row to remove it from this Sales Return.
              </p>
            )}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-[10px] uppercase">
                    <th className="text-left py-2 px-3 font-semibold">Product</th>
                    {sourceMode === 'oc' && <th className="text-center py-2 px-3 font-semibold">Orig Qty</th>}
                    <th className="text-center py-2 px-3 font-semibold">Return Qty</th>
                    <th className="text-center py-2 px-3 font-semibold">Unit</th>
                    <th className="text-left py-2 px-3 font-semibold">Reason</th>
                    <th className="text-left py-2 px-3 font-semibold">Stock Action</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {form.items.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50/30">
                      <td className="py-2 px-3">
                        {sourceMode === 'oc' ? (
                          <span className="font-medium">{item.product_name}</span>
                        ) : (
                          <ProductSearchInput
                            value={item.product_name}
                            productId={item.product_id}
                            onSelect={(product) => selectItemProduct(i, product)}
                            onChange={(text) => { updateItem(i, 'product_name', text); updateItem(i, 'product_id', ''); }}
                            onClear={() => { updateItem(i, 'product_name', ''); updateItem(i, 'product_id', ''); }}
                            placeholder="Search product by name or SKU..."
                            testId={`sr-item-name-${i}`}
                          />
                        )}
                      </td>
                      {sourceMode === 'oc' && <td className="py-2 px-3 text-center text-gray-500">{item.original_qty}</td>}
                      <td className="py-2 px-3">
                        <Input type="number" min={1} value={item.qty} onChange={e => updateItem(i, 'qty', parseInt(e.target.value) || 0)}
                          className="text-xs h-8 w-20 mx-auto text-center" data-testid={`sr-item-qty-${i}`} />
                      </td>
                      <td className="py-2 px-3 text-center text-gray-500">{item.unit}</td>
                      <td className="py-2 px-3">
                        <select value={item.reason} onChange={e => updateItem(i, 'reason', e.target.value)}
                          data-testid={`sr-item-reason-${i}`}
                          className="px-2 py-1 text-[11px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200">
                          {RETURN_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td className="py-2 px-3">
                        <select value={item.stock_action} onChange={e => updateItem(i, 'stock_action', e.target.value)}
                          data-testid={`sr-item-action-${i}`}
                          className="px-2 py-1 text-[11px] border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200">
                          {STOCK_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </td>
                      <td className="py-2 px-1">
                        {form.items.length > 1 && (
                          <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-600"
                            data-testid={`sr-remove-item-${i}`}
                            title={sourceMode === 'oc' ? 'Remove this item from the return (e.g. it was not returned)' : 'Remove row'}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
            <Textarea rows={3} value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes about the return..." data-testid="sr-notes" className="text-xs" />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-3 border-t">
            <Button variant="outline" onClick={() => setView('list')} data-testid="sr-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="sr-submit-btn"
              className="gap-1.5" style={{ backgroundColor: '#24549c' }}>
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Create Sales Return
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== DETAIL VIEW ====================
  if (view === 'detail' && selectedReturn) {
    const sr = selectedReturn;
    return (
      <div data-testid="sales-return-detail">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('list'); setSelectedReturn(null); }} className="p-1.5 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div>
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{sr.sr_number}</h2>
              <p className="text-[11px] text-gray-500">Sales Return {sr.linked_oc_number ? `(from ${sr.linked_oc_number})` : '(Standalone)'}</p>
            </div>
            <span className={`ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[sr.status] || 'bg-gray-100'}`}>{sr.status}</span>
          </div>
          <div className="flex items-center gap-2">
            {canVerify && sr.status === 'Draft' && (
              <>
                <button data-testid="sr-verify-btn" onClick={() => { setActionDialog({ type: 'verify', sr }); setActionNotes(''); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg bg-blue-600 hover:bg-blue-700">
                  <Truck className="w-3.5 h-3.5" /> Verify (Logistics)
                </button>
                <button data-testid="sr-reject-verify-btn" onClick={() => { setActionDialog({ type: 'verify', sr, reject: true }); setActionNotes(''); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </>
            )}
            {canApprove && sr.status === 'Verified' && (
              <>
                <button data-testid="sr-approve-btn" onClick={() => { setActionDialog({ type: 'approve', sr }); setActionNotes(''); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg bg-green-600 hover:bg-green-700">
                  <Check className="w-3.5 h-3.5" /> Approve (Accounts)
                </button>
                <button data-testid="sr-reject-approve-btn" onClick={() => { setActionDialog({ type: 'approve', sr, reject: true }); setActionNotes(''); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </>
            )}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Return Details</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-gray-400">SR Number</span><p className="font-semibold text-gray-900">{sr.sr_number}</p></div>
              <div><span className="text-gray-400">Date</span><p className="font-semibold text-gray-900">{sr.date}</p></div>
              <div><span className="text-gray-400">Customer</span><p className="font-semibold text-gray-900">{sr.customer_name}</p></div>
              <div><span className="text-gray-400">Branch</span><p className="font-semibold text-gray-900">{(branches.find(b => b.id === sr.branch) || {}).name || sr.branch || '-'}</p></div>
              <div><span className="text-gray-400">Linked OC</span><p className="font-semibold text-[#24549c]">{sr.linked_oc_number || '-'}</p></div>
              <div><span className="text-gray-400">Created By</span><p className="font-semibold text-gray-900">{sr.created_by}</p></div>
            </div>
            {sr.notes && (
              <div className="pt-2 border-t">
                <span className="text-[10px] text-gray-400 uppercase">Notes</span>
                <p className="text-xs text-gray-700 mt-1">{sr.notes}</p>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Documents</h3>
              {['Draft', 'Verified'].includes(sr.status) && (
                <label className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer border border-blue-200">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Upload
                  <input type="file" className="hidden" onChange={handleUpload} data-testid="sr-upload-doc" />
                </label>
              )}
            </div>
            {(sr.documents || []).length === 0 ? (
              <p className="text-xs text-gray-400 italic">No documents attached</p>
            ) : (
              <div className="space-y-2">
                {sr.documents.map((doc, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <a href={doc.url || doc.download_url || '#'} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline truncate flex-1">{doc.filename || doc.name || `Document ${i + 1}`}</a>
                    {['Draft', 'Verified'].includes(sr.status) && (
                      <button onClick={() => handleRemoveDoc(i)} className="p-1 text-red-400 hover:text-red-600 ml-2" data-testid={`sr-remove-doc-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5">
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">Return Items</h3>
          <table className="w-full text-xs" data-testid="sr-detail-items">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-[10px] uppercase">
                <th className="text-left py-2 px-3 font-semibold">#</th>
                <th className="text-left py-2 px-3 font-semibold">Product</th>
                <th className="text-center py-2 px-3 font-semibold">Qty</th>
                <th className="text-center py-2 px-3 font-semibold">Unit</th>
                <th className="text-left py-2 px-3 font-semibold">Reason</th>
                <th className="text-left py-2 px-3 font-semibold">Stock Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(sr.items || []).map((item, i) => (
                <tr key={i}>
                  <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                  <td className="py-2 px-3 font-medium text-gray-900">{item.product_name}</td>
                  <td className="py-2 px-3 text-center font-semibold">{item.qty}</td>
                  <td className="py-2 px-3 text-center text-gray-500">{item.unit}</td>
                  <td className="py-2 px-3">
                    <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">{item.reason}</span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                      item.stock_action === 'Restock' ? 'bg-green-50 text-green-700 border border-green-200' :
                      item.stock_action === 'Quarantine' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                      'bg-red-50 text-red-700 border border-red-200'
                    }`}>{item.stock_action}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Audit Trail */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Audit Trail
          </h3>
          <div className="space-y-3">
            {(sr.status_history || []).map((entry, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${
                  entry.status === 'Rejected' ? 'bg-red-500' :
                  entry.status === 'Approved' ? 'bg-green-500' :
                  entry.status === 'Verified' ? 'bg-blue-500' :
                  'bg-gray-400'
                }`} />
                <div>
                  <p className="text-xs font-semibold text-gray-900">{entry.status}</p>
                  <p className="text-[10px] text-gray-500">{entry.by} ({entry.role}) — {new Date(entry.at).toLocaleString()}</p>
                  {entry.notes && <p className="text-[10px] text-gray-600 mt-0.5 italic">{entry.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Dialog */}
        {actionDialog && (
          <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
            <DialogContent className="max-w-md" data-testid="sr-action-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {actionDialog.reject ? (
                    <><AlertTriangle className="w-4 h-4 text-red-500" /> Reject Sales Return</>
                  ) : actionDialog.type === 'verify' ? (
                    <><Truck className="w-4 h-4 text-blue-600" /> Verify Sales Return (Logistics)</>
                  ) : (
                    <><Check className="w-4 h-4 text-green-600" /> Approve Sales Return (Accounts)</>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg text-xs">
                  <p><strong>SR:</strong> {actionDialog.sr.sr_number}</p>
                  <p><strong>Customer:</strong> {actionDialog.sr.customer_name}</p>
                  <p><strong>Items:</strong> {(actionDialog.sr.items || []).length} items</p>
                </div>
                {!actionDialog.reject && actionDialog.type === 'approve' && (
                  <div className="p-3 bg-amber-50 rounded-lg text-[10px] text-amber-700 border border-amber-200">
                    <strong>Stock Impact:</strong> Upon approval, stock will be updated per each item's Stock Action (Restock / Quarantine / Scrap).
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
                  <Textarea rows={3} value={actionNotes} onChange={e => setActionNotes(e.target.value)}
                    placeholder="Add verification/approval notes..." data-testid="sr-action-notes" className="text-xs" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
                {actionDialog.reject ? (
                  <Button onClick={() => handleAction('reject')} disabled={actionLoading}
                    data-testid="sr-confirm-reject-btn" className="bg-red-600 hover:bg-red-700 gap-1.5">
                    {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                    Reject
                  </Button>
                ) : (
                  <Button onClick={() => handleAction(actionDialog.type)} disabled={actionLoading}
                    data-testid="sr-confirm-action-btn"
                    className={`gap-1.5 ${actionDialog.type === 'verify' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}>
                    {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {actionDialog.type === 'verify' ? 'Verify' : 'Approve'}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  return null;
};

export default SalesReturnModule;
